import type { Prayer } from "./types";

export const prayer: Prayer = {
  slug: "prayer-for-forgiveness",
  title: "A prayer for forgiveness — both directions",
  h1: "A prayer for forgiveness",
  description:
    "Two prayers: one for what you did, one for what was done to you. Four passages, and an honest word about the forgiveness that has not taken yet.",
  targets: [
    "prayer for forgiveness",
    "prayer to forgive someone",
    "prayer asking god for forgiveness",
    "prayer when you cannot forgive",
  ],
  published: "2026-09-03",
  standfirst:
    "The word covers two different jobs, and the harder one is almost never the one people came looking for.",
  takeaways: [
    'Confession is specific or it is nothing. "Sorry for everything" is a way of not naming the thing.',
    "Forgiving is a decision before it is a feeling, and the feeling can take years to arrive after the decision has been made properly.",
    "Forgiveness is not reconciliation and it is not access. You can forgive someone and still not be alone in a room with them.",
  ],
  when: [
    "After you have done the thing you promised yourself you would not do again.",
    "When you have apologised and been forgiven and cannot let yourself have it.",
    "About somebody you have forgiven three times already and can feel it coming back.",
    "When you are being told to forgive by somebody who was not hurt.",
  ],
  prayer: [
    "God, this is what I did. I am naming it rather than gesturing at it, because I have noticed that gesturing at it lets me keep it.",
    "I am not going to explain the circumstances. There were some. They are not the point tonight.",
    "Forgive it, and then let me actually put it down — I have a habit of asking and then carrying it anyway, which looks like humility and is closer to not believing you.",
    "And there is this other thing, which was done to me. I have said I forgave it. Some days I have. I am bringing it back because it came back.",
    "Make me willing before I am able. Start there.",
  ],
  short: "God, this is what I did. Forgive it, and let me put it down.",
  honest: [
    "This prayer does not undo consequences. Forgiveness and repair are different, and a debt that has been forgiven may still have to be paid — the apology does not delete the money, the job, or the trust.",
    "It also does not require you to feel forgiving. Deciding to forgive while still angry is not hypocrisy; it is the normal order, and treating the feeling as the test keeps people stuck for years.",
    "And forgiving does not mean returning. Nothing in these passages requires the person who hurt you to be given the same access they had before, and anyone using them that way is misusing them.",
  ],
  verses: [
    {
      verse: {
        text: "For if you forgive men their trespasses, your heavenly Father will also forgive you. But if you don't forgive men their trespasses, neither will your Father forgive your trespasses.",
        ref: "Matthew 6:14–15",
      },
      note: "The hardest sentence in the Sermon on the Mount, and it is the only line of the Lord's Prayer Jesus goes back to explain. It is not a transaction where your forgiving buys his. It is closer to a description of plumbing: a person who has genuinely received it cannot hold it shut against somebody else, and the holding shut is evidence about where they are.",
    },
    {
      verse: {
        text: "And be kind to one another, tender hearted, forgiving each other, just as God also in Christ forgave you.",
        ref: "Ephesians 4:32",
      },
      note: "The standard set is not fairness but a precedent, and the verse immediately before it is about getting rid of bitterness and shouting — this is the alternative offered to a specific ugly way of living together. Tender hearted is the demanding word: it asks for softness towards the person, which is a great deal more than not retaliating.",
    },
    {
      verse: {
        text: "bearing with one another, and forgiving each other, if any man has a complaint against any; even as Christ forgave you, so you also do.",
        ref: "Colossians 3:13",
      },
      note: "If any man has a complaint against any — the assumption is that the complaints are real, not imagined or exaggerated. This is not counsel to decide you were not actually wronged. It is counsel about what to do with a legitimate grievance, which is a much rarer and more useful thing.",
    },
    {
      verse: {
        text: 'Jesus said, "Father, forgive them, for they don\'t know what they are doing."',
        ref: "Luke 23:34",
      },
      note: "Said during the act, not afterwards, and about people who were still doing it. The reason offered — they do not know — is not an excuse handed to the soldiers so much as an appeal made over their heads. It is the hardest model in the book, and it is worth noticing that even here the sentence is addressed to God rather than to them.",
    },
  ],
  body: [
    {
      type: "h2",
      text: "Name it specifically",
      id: "name-it-specifically",
    },
    {
      type: "p",
      text: "Confession that stays general is a way of keeping the thing. Sorry for everything, sorry for how I have been, sorry for my many failings — none of it costs anything, and none of it can be answered, because there is nothing there to forgive.",
    },
    {
      type: "p",
      text: "Say what you did. To God first if that is easier, and then, where it involves somebody else, to them, without the paragraph of context that turns an apology into a defence.",
    },
    {
      type: "h2",
      text: "Willing before able",
      id: "willing-before-able",
    },
    {
      type: "p",
      text: "The most useful thing anybody says about the other direction is that you can start further back than forgiving. If you cannot forgive, ask to be made willing to. If you cannot manage that, ask to be made willing to be made willing.",
    },
    {
      type: "p",
      text: "This is not a trick. It is an accurate description of how a hard forgiveness actually proceeds — as a decision revisited a great many times, each one a little less expensive than the last.",
    },
    {
      type: "h2",
      text: "What forgiving is not",
      id: "what-forgiving-is-not",
    },
    {
      type: "p",
      text: "It is not forgetting, it is not saying it did not matter, and it is not restoring the relationship to where it was. Those are separate things and only one of them is being asked of you here.",
    },
    {
      type: "note",
      text: "Reconciliation takes two people and requires the other one to have changed. Forgiveness takes one and does not. Confusing them is how people end up back in situations they were right to leave.",
    },
    {
      type: "p",
      text: "And the version people find hardest is the one aimed at themselves. If you have asked and been forgiven and are still carrying it, the thing to bring is not the original offence — it is the {refusal to put it down}.",
      links: {
        "refusal to put it down": "/verses/bible-verses-about-forgiveness",
      },
    },
  ],
  faq: [
    {
      q: "How do I pray for forgiveness?",
      a: "Name the specific thing rather than apologising in general, ask for it to be forgiven, and then stop carrying it. The last part is where most people get stuck.",
    },
    {
      q: "What if I cannot forgive someone?",
      a: "Start further back and ask to be made willing to. Forgiving is a decision that gets revisited many times, and the feeling usually arrives long after the first decision — treating the feeling as the test keeps people stuck for years.",
    },
    {
      q: "Does forgiving someone mean I have to see them again?",
      a: "No. Forgiveness takes one person; reconciliation takes two and requires the other to have changed. Nothing in these passages requires you to give somebody the access they had before.",
    },
  ],
  relatedPrayers: ["prayer-for-anger", "prayer-for-anxiety"],
  related: ["phone-rules-for-christian-families"],
  relatedTopics: [
    "bible-verses-about-forgiveness",
    "bible-verses-about-peace",
    "bible-verses-about-gods-love",
  ],
  relatedPassages: ["psalm-23"],
};
